/**
 * Query scope and chip definitions for the pre-consultation queue.
 *
 * Separate from `PreConsultationTab` so the tab file exports only its component
 * — both for fast refresh, and because these are the parts worth testing without
 * mounting a table.
 */

import { Appointment, Meta } from '@medplum/fhirtypes';
import { appointmentTypeTokens, CONSULTATION_APPOINTMENT_CODES } from '@bariendo/lib/constants';
import { PAC_INTRO_CALL_CODE } from '@bariendo/lib/patient-access';
import { appointmentIsUpcoming, ListAppointment } from '@bariendo/lib/pac-list-appointments';
import { isHighLikelihoodNoConsult } from '@bariendo/lib/pac-pre-consultation-list';
import { ChipFilterDefinition } from '@/components/FilterChips/useChipFilters';
import { isWithinListRange, ListRange, ListRangePresetId } from '@/components/Hub/listRange';
import {
  appointmentQueueSubscriptionParams,
  buildAppointmentQueueFilter,
  hasLatestConsultTag,
  isInAppointmentQueueScope,
  LATEST_CONSULT_TAG_CLAUSE,
} from './appointmentQueueScope';
import { PreConsultationLeadRow } from './preConsultationRows';

/**
 * Every appointment that can put a lead in this queue.
 *
 * The intro call is first for history, not for volume: production has never
 * recorded one. Since Intake 1.1 the lead self-schedules a `pn-consultation-visit`,
 * and since BAR-2775 the PAC team books one too (older leads hold the NP's
 * `app-initial-consultation-visit`), so scoping
 * this query to the intro call alone is what kept the tab empty through a day of
 * signups. Reuses `CONSULTATION_APPOINTMENT_CODES` — the same list the procedure
 * stage reads — so the tab and the Pre-consultation pill cannot disagree about
 * what counts as a booked consult.
 */
const PRE_CONSULTATION_TYPE_TOKENS = appointmentTypeTokens([PAC_INTRO_CALL_CODE, ...CONSULTATION_APPOINTMENT_CODES]);

/**
 * The queue's scope, server-side. `noshow` is deliberately NOT excluded — a
 * no-showed call keeps the lead in this queue, and the Status column says so.
 */
export function buildPreConsultationFilter(range: ListRange): string {
  return buildAppointmentQueueFilter(PRE_CONSULTATION_TYPE_TOKENS, range);
}

const CONSULT_TYPE_TOKENS = appointmentTypeTokens(CONSULTATION_APPOINTMENT_CODES);

/** Still ahead: not missed, attended, cancelled or entered in error. */
const UPCOMING_STATUSES: readonly Appointment['status'][] = [
  'proposed',
  'pending',
  'booked',
  'arrived',
  'checked-in',
  'waitlist',
];

/**
 * The Intake hub's scope (BAR-2811): NP and PN consults in a forward range that
 * haven't been missed or attended. No intro calls; production has never booked one.
 *
 * Only `latest-consult`s, so the server pages patients (BAR-2880). A patient's
 * latest live consult is their upcoming one, so no tag of our own is needed.
 */
export function buildUpcomingConsultFilter(range: ListRange): string {
  const scope = buildAppointmentQueueFilter(CONSULT_TYPE_TOKENS, range, { statuses: UPCOMING_STATUSES });
  return `(${scope} and ${LATEST_CONSULT_TAG_CLAUSE})`;
}

/** The hub's subscription: consult types only. */
export function upcomingConsultSubscriptionParams(): Record<string, string> {
  return appointmentQueueSubscriptionParams(CONSULT_TYPE_TOKENS);
}

/** The query's rules in the browser; a consult in progress still counts as upcoming. */
export function isUpcomingConsult(
  appointment: ListAppointment & { meta?: Meta },
  range: ListRange,
  now: Date
): boolean {
  return (
    hasLatestConsultTag(appointment) &&
    appointmentIsUpcoming(appointment, now) &&
    isWithinListRange(appointment.start, range)
  );
}

/** What the tab subscribes to — see `appointmentQueueScope` for why it is coarser than the query. */
export function preConsultationSubscriptionParams(): Record<string, string> {
  return appointmentQueueSubscriptionParams(PRE_CONSULTATION_TYPE_TOKENS);
}

/** The query's status and range rules, for what arrives by subscription. */
export function isInPreConsultationScope(appointment: ListAppointment, range: ListRange): boolean {
  return isInAppointmentQueueScope(appointment, range);
}

/**
 * The window this queue actually wants, from the one the range control offers.
 *
 * Every relative preset ends today, which is correct for post-consultation —
 * it ranges over consults that already happened — and exactly wrong here: this
 * tab's population is *upcoming* consults, so an upper bound of today excludes
 * all of it. Today's sign-ups book into next week; capping at today would leave
 * the tab as empty as the intro-call scope did.
 *
 * A custom range is two dates a rep typed, so both are honoured.
 */
export function preConsultationScope(range: ListRange, presetId: ListRangePresetId): ListRange {
  return presetId === 'custom' ? range : { from: range.from };
}

export const PRE_CONSULTATION_CHIPS: ChipFilterDefinition<PreConsultationLeadRow>[] = [
  {
    id: 'inactive-hidden',
    label: 'Inactive hidden',
    defaultOn: true,
    removable: true,
    predicate: (row) => row.activeLead.status === 'active',
  },
  {
    id: 'high-likelihood-no-consult',
    label: 'High likelihood · no consult',
    predicate: (row) => isHighLikelihoodNoConsult(row),
  },
];
